import {Dialog, DialogBackdrop, DialogPanel, DialogTitle, Switch} from "@headlessui/react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faTimes} from "@fortawesome/free-solid-svg-icons";
import {DateTimePickerInput} from "../../Form/DateTimePickerInput";
import {TextAreaInput} from "../../Form/TextAreaInput";
import {PrimaryButton} from "../../Buttons/PrimaryButton";
import * as React from "react";
import {useTranslation} from "react-i18next";
import * as yup from "yup";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {useBoundStore} from "../../../Store/store";
import {NumberInput} from "../../Form/NumberInput";
import dayjs from "dayjs";
import toast from "react-hot-toast";
import {ControlledSwitch} from "../../Form/ControlledSwitch";
import {SelectInput} from "../../Form/SelectInput";
import {useEffect, useState} from "react";
import {DangerButton} from "../../Buttons/DangerButton";
import {DeleteConfirmDialog} from "../Confirmation/DeleteConfirmDialog";
import DatePicker from "react-datepicker";
import nl from "date-fns/locale/nl";

export const UpdateShiftDialog = (props) => {
    const {
        open,
        onClose,
        customerId,
        shift,
        branches,
    } = props;

    const {t} = useTranslation();

    const updateShift = useBoundStore((state) => state.updateShift);
    const deleteShift = useBoundStore((state) => state.deleteShift);
    const branchesFromStore = useBoundStore((state) => state.branchesNotPaginated);
    const getMyBranchesNotPaginated = useBoundStore((state) => state.getMyBranchesNotPaginated);
    const branchesNotPaginated = branches ?? branchesFromStore;

    const [openDeleteDialog, setOpenDeleteDialog] = React.useState(false);

    const shiftApplicationPossibilities = [
        {id: 1, value: 'both', label: t('shifts.both')},
        {id: 2, value: 'only_verpleegkundige', label: t('shifts.filter_only_nurse')},
        {id: 3, value: 'only_zorgkundige', label: t('shifts.filter_only_caregiver')},
    ];

    const statutenOptions = [
        {id: 1, value: 'freelance', label: t('statuten.freelance')},
        {id: 2, value: 'interim', label: t('statuten.interim')},
        {id: 3, value: 'student', label: t('statuten.student')},
        {id: 4, value: 'flexi_job', label: t('statuten.flexi_job')},
    ];

    useEffect(() => {
        if (branches || !open) return;
        getMyBranchesNotPaginated();
    }, [open, branches, getMyBranchesNotPaginated]);

    useEffect(() => {
        if(shift) {
            reset({
                ...shift,
                start_time: dayjs(shift.start_time).format('YYYY-MM-DD HH:mm:ss'),
                end_time: dayjs(shift.end_time).format('YYYY-MM-DD HH:mm:ss'),
                break_duration: shift.break_duration,
                is_published: shift.is_published,
                remarks: shift.remarks,
                shift_repeats_enabled: shift.shift_repeats_enabled,
                shift_repeats: shift.shift_repeats,
                shift_applications_possibility: shift.shift_applications_possibility,
                allowed_statuten: shift.allowed_statuten?.length ? shift.allowed_statuten : ['freelance'],
                branch_id: shift?.branch?.id,
            })
        }
    }, [shift]);

    const schema = yup.object({
        start_time: yup.string().required(),
        end_time: yup
            .string()
            .required()
            .test(
                "is-after-start",
                "De eindtijd moet na de starttijd zijn",
                function (value) {
                    const { start_time } = this.parent;
                    if (!start_time || !value) return true;
                    return dayjs(value).isAfter(dayjs(start_time));
                }
            ),
        break_duration: yup.string().nullable(),
        is_published: yup.boolean(),
        remarks: yup.string().nullable(),
        shift_repeats_enabled: yup.boolean().nullable(),
        shift_repeats: yup.array().nullable(),
        allowed_statuten: yup.array().of(yup.string()).min(1, t('shifts_module.allowed_statuten_min')).required(),
        branch_id: yup.string().required(),
    }).required()

    const {
        register,
        handleSubmit,
        formState: {errors},
        getValues,
        setValue,
        control,
        reset
    } = useForm({
        resolver: yupResolver(schema),
        reValidateMode: 'onBlur',
        mode: 'onChange',
        defaultValues:{
            customer_id: customerId,
            start_time: shift?.start_time,
            end_time: shift?.end_time,
            break_duration: shift?.break_duration,
            is_published: shift?.is_published,
            remarks: shift?.remarks,
            shift_applications_possibility: shift?.shift_applications_possibility ?? 'both',
            allowed_statuten: shift?.allowed_statuten?.length ? shift.allowed_statuten : ['freelance'],
        }
    });

    const onSubmit = (data) => {
        const transformedData = {
            ...data,
            start_time: dayjs(data.start_time).format('YYYY-MM-DD HH:mm:ss'),
            end_time: dayjs(data.end_time).format('YYYY-MM-DD HH:mm:ss'),
            is_published: data.is_published === "" ? false : data.is_published === true,
            shift_applications_possibility: data.shift_applications_possibility ?? 'both',
        }

        updateShift(shift?.id , transformedData)
            .then(() => {
                toast.success(t('shifts.toast_saved'));
                onClose();
            })
            .catch((error) => {
                console.error('Error creating customer', error);
            })
    }

    return (
        <>
            <Dialog className="relative z-50" open={open} onClose={onClose}>
                <DialogBackdrop
                    transition
                    className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity duration-500 ease-in-out data-[closed]:opacity-0"
                />

                <div className="fixed inset-0 overflow-y-scroll">
                    <div className="absolute inset-0 overflow-y-scroll">
                        <div className="pointer-events-none p-4 rounded-lg fixed inset-y-0 right-0 flex max-w-full">
                            <DialogPanel
                                transition
                                className="pointer-events-auto w-screen max-w-lg transform transition duration-500 ease-in-out data-[closed]:translate-x-full sm:duration-700"
                            >
                                <div className="flex h-full rounded-2xl flex-col overflow-y-scroll bg-white py-6 shadow-xl">
                                    <div className="px-4 sm:px-6">
                                        <div className="flex items-start justify-between">
                                            <DialogTitle className="text-base font-semibold leading-6 text-gray-900">
                                                {t('shifts.dialog_update_shift_title')}
                                            </DialogTitle>
                                            <div className="ml-3 flex h-7 items-center">
                                                <button
                                                    type="button"
                                                    className="relative rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
                                                    onClick={() => onClose()}
                                                >
                                                    <span className="absolute -inset-2.5" />
                                                    <span className="sr-only">Close panel</span>
                                                    <FontAwesomeIcon icon={faTimes} className="h-6 w-6" aria-hidden="true" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="relative mt-6 flex-1 px-4 sm:px-6">
                                        <form className="w-full" onSubmit={handleSubmit(onSubmit)}>

                                            <div className={'grid grid-cols-1 md:grid-cols-2 mb-1 md:space-x-4'}>
                                                <DateTimePickerInput
                                                    label={'Start time'}
                                                    name={'start_time'}
                                                    setValue={setValue}
                                                    control={control}
                                                    currentValue={getValues('start_time')}
                                                    translationIndex={'general.fields.start_time'}
                                                    errors={errors}
                                                />
                                                <DateTimePickerInput
                                                    label={'End time'}
                                                    name={'end_time'}
                                                    setValue={setValue}
                                                    control={control}
                                                    currentValue={getValues('start_time')}
                                                    translationIndex={'general.fields.end_time'}
                                                    errors={errors}
                                                />
                                            </div>

                                            {
                                                branchesNotPaginated.length > 1 && (
                                                    <div className="mb-2">
                                                        <SelectInput
                                                            name={'branch_id'}
                                                            options={branchesNotPaginated.map((branch) => {
                                                                return {
                                                                    id: branch.id,
                                                                    value: branch.id,
                                                                    label: branch.company_name
                                                                }
                                                            })}
                                                            setValue={setValue}
                                                            control={control}
                                                            value={getValues('branch_id') ?? null}
                                                            placeholder={t('shifts.select_branch')}
                                                            translationIndex={t('shifts.branches')}
                                                            errors={errors}
                                                        />
                                                    </div>
                                                )
                                            }

                                            <div className="mb-2">
                                                <SelectInput
                                                    name={'shift_applications_possibility'}
                                                    options={shiftApplicationPossibilities}
                                                    setValue={setValue}
                                                    control={control}
                                                    value={getValues('shift_applications_possibility') ?? null}
                                                    placeholder={t('general.fields.shift_applications_possibility')}
                                                    translationIndex={'shifts.shift_applications_possibility'}
                                                    isDisabled={!!shift?.freelancer_id}
                                                />
                                            </div>

                                            <div className="mb-2">
                                                <SelectInput
                                                    name={'allowed_statuten'}
                                                    options={statutenOptions}
                                                    multiple={true}
                                                    setValue={setValue}
                                                    control={control}
                                                    currentValue={getValues('allowed_statuten') ?? ['freelance']}
                                                    translationIndex={'shifts.allowed_statuten'}
                                                    helperText={shift?.freelancer_id ? t('shifts.allowed_statuten_locked_helper') : t('shifts.allowed_statuten_helper')}
                                                    errors={errors}
                                                    isDisabled={!!shift?.freelancer_id}
                                                />
                                            </div>

                                            <div className="mb-2">
                                                <NumberInput
                                                    register={register}
                                                    name={'break_duration'}
                                                    translationIndex={'general.fields.break_duration'}
                                                    errors={errors}
                                                />
                                            </div>
                                            <div className="mb-2">
                                                <TextAreaInput
                                                    register={register}
                                                    name={'remarks'}
                                                    translationIndex={'general.fields.remarks'}
                                                    errors={errors}
                                                />
                                            </div>
                                            <div className="mb-2">
                                                <div className="flex flex-row justify-start">
                                                    <ControlledSwitch
                                                        control={control}
                                                        name={'is_published'}
                                                        getValues={getValues}
                                                        setValue={setValue}
                                                        errors={errors}
                                                        translationIndex={'general.fields.is_published'}
                                                    />
                                                </div>
                                            </div>

                                            <div className={'grid grid-cols-1 md:grid-cols-2 gap-4 mt-4'}>
                                                <DangerButton onClick={() => {
                                                    onClose();
                                                    setOpenDeleteDialog(true);
                                                }} className={'order-2 md:order-1'}>
                                                    {t('general.actions.delete')}
                                                </DangerButton>
                                                <PrimaryButton onClick={handleSubmit(onSubmit)} className={'order-1 md:order-2'}>
                                                    Shift opslaan
                                                </PrimaryButton>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </DialogPanel>
                        </div>
                    </div>
                </div>
            </Dialog>

            <DeleteConfirmDialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)} onConfirm={() => {
                deleteShift(shift?.id).then(() => {
                    setOpenDeleteDialog(false);
                    onClose();
                    toast.success(t('shifts.toast_deleted'));
                })
            }}/>
        </>

    )
}